// Modern (D-style) Masters tab: list + profile detail with hero, KPIs,
// retention ring, payout hero.

import { useEffect, useMemo, useState } from "react";
import { api } from "../api.js";
import {
  PeriodPicker,
  periodToIsoRange,
  type PeriodValue,
} from "../components/PeriodPicker.js";
import { ConfirmSheet } from "../components/ConfirmSheet.js";
import { Toast, type ToastMsg } from "../components/Toast.js";
import { useScrollMemory } from "../scrollMemory.js";

interface MasterRow {
  id: string;
  name: string;
  telegramId: string | null;
  telegramUsername: string | null;
  active: boolean;
  starred: boolean;
  lastVisitAt: string | null;
}

function nzInitials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0] || "")[0] + ((p[1] || "")[0] || "")).toUpperCase();
}
function nzAvatar(name: string): string {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const hue = h % 360;
  return `linear-gradient(150deg, hsl(${hue} 52% 70%), hsl(${(hue + 26) % 360} 50% 58%))`;
}

export function MastersTabModern() {
  const [items, setItems] = useState<MasterRow[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reportsMasterId, setReportsMasterId] = useState<string | null>(null);
  const [sub, setSub] = useState<"masters" | "reports">("masters");
  const [addOpen, setAddOpen] = useState(false);
  const captureScroll = useScrollMemory(!!selectedId || !!reportsMasterId);
  const openMaster = (id: string) => {
    captureScroll();
    setSelectedId(id);
  };
  const openReports = (id: string) => {
    captureScroll();
    setReportsMasterId(id);
  };

  function load() {
    api
      .masters()
      .then((data) => setItems(data as MasterRow[]))
      .catch(() => setItems([]));
  }
  useEffect(() => {
    load();
  }, []);

  // Overlay-pattern щоб скрол + sub-tab лишались при поверненні з
  // MasterDetailModern або MasterCashReportsModern. Старі early-return
  // анмаунтили список → скрол скидався на верх.
  const overlayOpen = !!reportsMasterId || !!selectedId;
  const reportsMaster = items?.find((x) => x.id === reportsMasterId);
  return (
    <>
      <div style={{ display: overlayOpen ? "none" : "block" }}>
        <div className="nz-segwrap" style={{ marginTop: 6 }}>
          <button
            className="nz-seg"
            data-on={sub === "masters"}
            onClick={() => setSub("masters")}
          >
            💁‍♀️ Майстри
          </button>
          <button
            className="nz-seg"
            data-on={sub === "reports"}
            onClick={() => setSub("reports")}
          >
            📋 Звіти каси
          </button>
        </div>

        {sub === "masters" && (
          <>
            <button
              className="nz-create"
              style={{ marginTop: 14 }}
              onClick={() => setAddOpen(true)}
            >
              ➕ Додати майстра
            </button>
            <div className="nz-countline">
              <span>{items?.length ?? 0} майстрів</span>
            </div>
            {(items ?? []).map((m) => (
              <MasterBlock
                key={m.id}
                m={m}
                onOpen={() => openMaster(m.id)}
                onOpenReports={() => openReports(m.id)}
                onToggleStar={async () => {
                  await api.setMasterStar(m.id, !m.starred);
                  load();
                }}
              />
            ))}
            {addOpen && (
              <AddMasterSheet
                onClose={() => setAddOpen(false)}
                onCreated={() => {
                  setAddOpen(false);
                  load();
                }}
              />
            )}
          </>
        )}

        {sub === "reports" && <AllCashReportsView />}
      </div>
      {reportsMasterId && (
        <MasterCashReportsModern
          masterId={reportsMasterId}
          masterName={reportsMaster?.name ?? "Майстер"}
          onBack={() => setReportsMasterId(null)}
        />
      )}
      {selectedId && (
        <MasterDetailModern
          id={selectedId}
          onBack={() => setSelectedId(null)}
        />
      )}
    </>
  );
}

// ---- All cash reports — accordion per master ----

function AllCashReportsView() {
  const [data, setData] = useState<any[] | null>(null);
  useEffect(() => {
    api.allCashReports().then((d) => setData(d as any[])).catch(() => setData([]));
  }, []);

  const byMaster = useMemo(() => {
    if (!data) return [];
    type Bucket = { name: string; reports: any[]; sum: number; tips: number; unpaid: number };
    const map = new Map<string, Bucket>();
    for (const r of data) {
      const id = r.master?.id ?? "_";
      const name = r.master?.name ?? "(не привʼязано)";
      const bucket: Bucket = map.get(id) ?? { name, reports: [], sum: 0, tips: 0, unpaid: 0 };
      bucket.reports.push(r);
      bucket.sum += r.total ?? 0;
      bucket.tips += r.tips ?? 0;
      if (r.entries?.some((e: any) => !e.inPayout)) bucket.unpaid += 1;
      map.set(id, bucket);
    }
    return Array.from(map.entries()).map(([id, v]) => ({ id, ...v }));
  }, [data]);

  if (!data) return <div className="hint" style={{ marginTop: 14 }}>Завантаження…</div>;
  if (data.length === 0)
    return (
      <div className="nz-section-hint" style={{ textAlign: "center", padding: 30 }}>
        Поки нема жодного звіту каси.
      </div>
    );

  return (
    <>
      <div className="nz-summary">
        Тапни майстерку → розгорнуться її звіти. Знизу кнопка повного редактору.
      </div>
      {byMaster.map((m) => (
        <MasterReportAccModern key={m.id} m={m} />
      ))}
    </>
  );
}

function MasterReportAccModern({ m }: { m: { id: string; name: string; reports: any[]; sum: number; tips: number; unpaid: number } }) {
  const [open, setOpen] = useState(false);
  const [expandedReport, setExpandedReport] = useState<string | null>(null);
  // Локальна копія звітів, щоб optimistic update перерахував суми при редагуванні entry.
  const [reports, setReports] = useState(m.reports);
  useEffect(() => setReports(m.reports), [m.reports]);
  // Підтвердження видалення звіту цілком — id звіту що зараз у фокусі модалки.
  const [deleteConfirm, setDeleteConfirm] = useState<any | null>(null);
  // Підтвердження «відмінити оплачене» — звіт у фокусі модалки.
  const [unpayConfirm, setUnpayConfirm] = useState<any | null>(null);

  // Оптимістично позначити всі рядки звіту як неоплачені (unpay лише міняє
  // статус оплати — суми послуг/чайових ті самі).
  const markUnpaid = (reportId: string) => {
    setReports((rs) =>
      rs.map((r: any) =>
        r.id === reportId
          ? {
              ...r,
              entries: (r.entries ?? []).map((e: any) => ({
                ...e,
                inPayout: false,
              })),
            }
          : r,
      ),
    );
  };

  // Рядок видалено — прибрати зі списку і перерахувати суми звіту.
  const removeEntry = (reportId: string, entryId: string) => {
    setReports((rs) =>
      rs.map((r: any) => {
        if (r.id !== reportId) return r;
        const left = (r.entries ?? []).filter((e: any) => e.id !== entryId);
        return {
          ...r,
          entries: left,
          total: left.reduce((s: number, e: any) => s + Number(e.amount ?? 0), 0),
          tips: left.reduce((s: number, e: any) => s + Number(e.tip ?? 0), 0),
        };
      }),
    );
  };

  const updateEntry = (reportId: string, entryId: string, next: Partial<any>) => {
    setReports((rs) =>
      rs.map((r: any) => {
        if (r.id !== reportId) return r;
        const updated = (r.entries ?? []).map((e: any) =>
          e.id === entryId ? { ...e, ...next } : e,
        );
        return {
          ...r,
          entries: updated,
          total: updated.reduce(
            (s: number, e: any) => s + Number(e.amount ?? 0),
            0,
          ),
          tips: updated.reduce(
            (s: number, e: any) => s + Number(e.tip ?? 0),
            0,
          ),
        };
      }),
    );
  };

  return (
    <div className="nz-acc">
      <button className="nz-acc-head" onClick={() => setOpen((o) => !o)}>
        <div className="nz-row-av" style={{ background: nzAvatar(m.name) }}>{nzInitials(m.name)}</div>
        <div className="nz-acc-title">
          <div className="t">{m.name}</div>
          <div className="s">
            {reports.length} звітів · {uah(m.sum)} ₴
            {m.tips > 0 ? ` · 💸 ${uah(m.tips)}` : ""}
            {m.unpaid > 0 ? ` · 💰 ${m.unpaid} неоплач.` : ""}
          </div>
        </div>
        <span className="nz-chev" data-open={open}>
          <ChevronRight />
        </span>
      </button>
      {open && (
        <div className="nz-acc-body">
          {reports.slice(0, 20).map((r: any) => {
            const paid = r.entries?.every((e: any) => e.inPayout) ?? false;
            const isExp = expandedReport === r.id;
            return (
              <div className="nz-acc-report" key={r.id}>
                <button
                  className="rh"
                  onClick={() => setExpandedReport(isExp ? null : r.id)}
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: 0,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    padding: 0,
                    color: "inherit",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    {isExp ? "▾" : "▸"} 💰{" "}
                    {new Date(r.occurredOn).toLocaleDateString("uk-UA")}
                  </span>
                  <span className={"nz-paid " + (paid ? "yes" : "no")}>
                    {paid ? "✅ оплачено" : "💰 неоплач."}
                  </span>
                </button>
                <div className="rs">
                  {r.entries?.length ?? 0} послуг · {uah(r.total)} ₴
                  {r.tips > 0 ? ` · чай ${uah(r.tips)}` : ""}
                </div>
                {isExp && r.entries?.length > 0 && (
                  <div
                    style={{
                      marginTop: 9,
                      display: "flex",
                      flexDirection: "column",
                      gap: 5,
                    }}
                  >
                    {r.entries.map((e: any) => (
                      <CashEntryRow
                        key={e.id}
                        entry={e}
                        canEdit={true}
                        canRedo={true}
                        onSaved={(next) => updateEntry(r.id, e.id, next)}
                        onDeleted={() => removeEntry(r.id, e.id)}
                      />
                    ))}
                    {!paid && (
                      <button
                        type="button"
                        onClick={() => setDeleteConfirm(r)}
                        className="nz-report-delete"
                      >
                        🗑 Видалити цей звіт
                      </button>
                    )}
                    {paid && (
                      <button
                        type="button"
                        onClick={() => setUnpayConfirm(r)}
                        className="nz-report-unpay"
                      >
                        ↩️ Відмінити оплачене
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmSheet
        open={!!unpayConfirm}
        onClose={() => setUnpayConfirm(null)}
        title={
          unpayConfirm
            ? `Відмінити оплачене за ${new Date(unpayConfirm.occurredOn).toLocaleDateString("uk-UA")}?`
            : ""
        }
        body={
          unpayConfirm && (
            <>
              <p style={{ margin: "0 0 6px" }}>
                Майстер: <b>{m.name}</b><br />
                {unpayConfirm.entries?.length ?? 0} послуг ·{" "}
                <b>{uah(unpayConfirm.total)} ₴</b>
                {unpayConfirm.tips > 0 ? ` · чай ${uah(unpayConfirm.tips)} ₴` : ""}
              </p>
              <p style={{ margin: 0 }}>
                Усі рядки цього звіту повернуться в <b>«до виплати»</b>. Сума
                виплати перерахується, а якщо це був єдиний день у ній — виплата
                видалиться повністю. Гроші й записи не втрачаються — просто
                знімається статус «оплачено».
              </p>
            </>
          )
        }
        confirmLabel="↩️ Відмінити оплачене"
        tone="danger"
        onConfirm={async () => {
          if (!unpayConfirm) return;
          try {
            await api.unpayCashReport(unpayConfirm.id);
            markUnpaid(unpayConfirm.id);
            setUnpayConfirm(null);
          } catch (e) {
            alert(String(e));
          }
        }}
      />

      <ConfirmSheet
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={
          deleteConfirm
            ? `Видалити звіт за ${new Date(deleteConfirm.occurredOn).toLocaleDateString("uk-UA")}?`
            : ""
        }
        body={
          deleteConfirm && (
            <>
              <p style={{ margin: "0 0 6px" }}>
                Майстер: <b>{m.name}</b><br />
                Подано: {deleteConfirm.sentFull ?? deleteConfirm.sentDate}<br />
                {deleteConfirm.entries?.length ?? 0} послуг ·{" "}
                <b>{uah(deleteConfirm.total)} ₴</b>
                {deleteConfirm.tips > 0 ? ` · чай ${uah(deleteConfirm.tips)} ₴` : ""}
              </p>
              <p style={{ margin: 0 }}>
                Видалить <b>усі рядки</b> цього звіту з каси. Це невідновлювана дія
                — використовуй коли майстер помилково подав не той день / не того
                місяця. Якщо звіт уже у виплаті — спочатку видали виплату.
              </p>
            </>
          )
        }
        confirmLabel="🗑 Видалити звіт"
        tone="danger"
        onConfirm={async () => {
          if (!deleteConfirm) return;
          try {
            await api.deleteCashReport(deleteConfirm.id);
            setReports((rs) => rs.filter((x: any) => x.id !== deleteConfirm.id));
            setDeleteConfirm(null);
          } catch (e) {
            alert(String(e));
          }
        }}
      />
    </div>
  );
}

// ---- Per-master cash-reports (when tapped from master row btn) ----

function MasterCashReportsModern({
  masterId,
  masterName,
  onBack,
}: {
  masterId: string;
  masterName: string;
  onBack: () => void;
}) {
  const [reports, setReports] = useState<any[] | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<any | null>(null);
  const [unpayConfirm, setUnpayConfirm] = useState<any | null>(null);

  function load() {
    api.masterCashReports(masterId).then((d) => setReports(d as any[])).catch(() => setReports([]));
  }
  useEffect(load, [masterId]);

  // Рядок видалено — прибрати з усіх звітів і перерахувати суми.
  const removeEntry = (entryId: string) => {
    setReports((rs) =>
      rs
        ? rs.map((r) => {
            const left = (r.entries ?? []).filter((e: any) => e.id !== entryId);
            return {
              ...r,
              entries: left,
              total: left.reduce((s: number, e: any) => s + Number(e.amount ?? 0), 0),
              tips: left.reduce((s: number, e: any) => s + Number(e.tip ?? 0), 0),
            };
          })
        : rs,
    );
  };

  const updateEntry = (entryId: string, next: Partial<any>) => {
    setReports((rs) =>
      rs
        ? rs.map((r) => ({
            ...r,
            entries: r.entries?.map((e: any) =>
              e.id === entryId ? { ...e, ...next } : e,
            ),
            // recompute totals so user sees live sum
            total: (r.entries ?? [])
              .map((e: any) => (e.id === entryId ? { ...e, ...next } : e))
              .reduce((s: number, e: any) => s + Number(e.amount ?? 0), 0),
            tips: (r.entries ?? [])
              .map((e: any) => (e.id === entryId ? { ...e, ...next } : e))
              .reduce((s: number, e: any) => s + Number(e.tip ?? 0), 0),
          }))
        : rs,
    );
  };

  return (
    <>
      <button className="nz-backbtn" onClick={onBack}>← {masterName}</button>
      <div className="nz-sec-title" style={{ margin: "14px 4px 10px" }}>
        📋 Звіти каси · {masterName}
      </div>
      <div className="nz-sec-sub" style={{ margin: "0 4px 14px" }}>
        Тапни на послугу — відкриється редактор. Зміни зберігаються одразу.
      </div>
      {!reports && <div className="hint">Завантаження…</div>}
      {reports?.length === 0 && (
        <div className="nz-section-hint" style={{ textAlign: "center", padding: 30 }}>
          Майстерка ще не присилала касових звітів.
        </div>
      )}
      {reports?.map((r) => {
        const paid = r.entries?.every((e: any) => e.inPayout) ?? false;
        return (
          <div className="nz-acc-report" key={r.id} style={{ marginBottom: 10 }}>
            <div className="rh">
              <span>💰 {new Date(r.occurredOn).toLocaleDateString("uk-UA")} · {r.sentTime}</span>
              <span className={"nz-paid " + (paid ? "yes" : "no")}>
                {paid ? "✅ оплачено" : "💰 неоплач."}
              </span>
            </div>
            <div className="rs">
              {r.entries?.length ?? 0} послуг · {uah(r.total)} ₴
              {r.tips > 0 ? ` · чай ${uah(r.tips)}` : ""}
            </div>
            {r.entries?.length > 0 && (
              <div style={{ marginTop: 9, display: "flex", flexDirection: "column", gap: 5 }}>
                {r.entries.map((e: any) => (
                  <CashEntryRow
                    key={e.id}
                    entry={e}
                    canEdit={true}
                    canRedo={true}
                    onSaved={(next) => updateEntry(e.id, next)}
                    onDeleted={() => removeEntry(e.id)}
                  />
                ))}
                {!paid && (
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(r)}
                    className="nz-report-delete"
                  >
                    🗑 Видалити цей звіт
                  </button>
                )}
                {paid && (
                  <button
                    type="button"
                    onClick={() => setUnpayConfirm(r)}
                    className="nz-report-unpay"
                  >
                    ↩️ Відмінити оплачене
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      <ConfirmSheet
        open={!!unpayConfirm}
        onClose={() => setUnpayConfirm(null)}
        title={
          unpayConfirm
            ? `Відмінити оплачене за ${new Date(unpayConfirm.occurredOn).toLocaleDateString("uk-UA")}?`
            : ""
        }
        body={
          unpayConfirm && (
            <>
              <p style={{ margin: "0 0 6px" }}>
                Майстер: <b>{masterName}</b><br />
                {unpayConfirm.entries?.length ?? 0} послуг ·{" "}
                <b>{uah(unpayConfirm.total)} ₴</b>
                {unpayConfirm.tips > 0 ? ` · чай ${uah(unpayConfirm.tips)} ₴` : ""}
              </p>
              <p style={{ margin: 0 }}>
                Усі рядки цього звіту повернуться в <b>«до виплати»</b>. Сума
                виплати перерахується, а якщо це був єдиний день у ній — виплата
                видалиться повністю. Гроші й записи не втрачаються — просто
                знімається статус «оплачено».
              </p>
            </>
          )
        }
        confirmLabel="↩️ Відмінити оплачене"
        tone="danger"
        onConfirm={async () => {
          if (!unpayConfirm) return;
          try {
            await api.unpayCashReport(unpayConfirm.id);
            setUnpayConfirm(null);
            load();
          } catch (e) {
            alert(String(e));
          }
        }}
      />

      <ConfirmSheet
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={
          deleteConfirm
            ? `Видалити звіт за ${new Date(deleteConfirm.occurredOn).toLocaleDateString("uk-UA")}?`
            : ""
        }
        body={
          deleteConfirm && (
            <>
              <p style={{ margin: "0 0 6px" }}>
                Майстер: <b>{masterName}</b><br />
                Подано: {deleteConfirm.sentFull ?? deleteConfirm.sentDate}<br />
                {deleteConfirm.entries?.length ?? 0} послуг ·{" "}
                <b>{uah(deleteConfirm.total)} ₴</b>
                {deleteConfirm.tips > 0 ? ` · чай ${uah(deleteConfirm.tips)} ₴` : ""}
              </p>
              <p style={{ margin: 0 }}>
                Видалить <b>усі рядки</b> цього звіту з каси. Це невідновлювана дія —
                використовуй коли майстер помилково подав не той день / не того
                місяця. Якщо звіт уже у виплаті — спочатку видали виплату.
              </p>
            </>
          )
        }
        confirmLabel="🗑 Видалити звіт"
        tone="danger"
        onConfirm={async () => {
          if (!deleteConfirm) return;
          try {
            await api.deleteCashReport(deleteConfirm.id);
            setReports((rs) =>
              rs ? rs.filter((x: any) => x.id !== deleteConfirm.id) : rs,
            );
            setDeleteConfirm(null);
          } catch (e) {
            alert(String(e));
          }
        }}
      />
    </>
  );
}

function CashEntryRow({
  entry,
  onSaved,
  onDeleted,
  canEdit = true,
  canRedo = false,
}: {
  entry: any;
  onSaved: (next: Partial<{ service: string; amount: number; tip: number; discountPercent: number; isRedo: boolean }>) => void;
  /** Рядок видалено — батько прибирає його зі свого списку. */
  onDeleted?: () => void;
  /** Редагування послуги/ціни/чайових/знижки. Дозволено адміну (Таня
   *  виправляє помилки майстрів). Якщо false — тап нічого не робить. */
  canEdit?: boolean;
  /** Позначка «переробка» (redo). Дозволено адміну — про кожне
   *  перемикання не-власником власниця отримує повідомлення. */
  canRedo?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [service, setService] = useState(entry.service ?? "");
  const [amount, setAmount] = useState(String(entry.amount ?? 0));
  const [tip, setTip] = useState(String(entry.tip ?? 0));
  const [discount, setDiscount] = useState(String(entry.discountPercent ?? 0));
  const [busy, setBusy] = useState(false);

  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      const next = {
        service: service.trim(),
        amount: Number(amount) || 0,
        tip: Number(tip) || 0,
        discountPercent: Number(discount) || 0,
      };
      await api.editCashEntry(entry.id, next);
      onSaved(next);
      setEditing(false);
    } catch (e) {
      alert(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (busy) return;
    if (
      !window.confirm(
        `Видалити рядок «${entry.service ?? "?"} — ${uah(entry.amount)} ₴»?\n\n` +
          `Рядок зникне зі звіту і з каси. Скасувати не можна.`,
      )
    )
      return;
    setBusy(true);
    try {
      await api.deleteCash(entry.id);
      onDeleted?.();
    } catch (e) {
      alert(String(e));
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <div
        style={{
          background: "var(--surface-2)",
          borderRadius: 11,
          padding: 11,
          marginTop: 4,
          border: "1px solid var(--rose-200)",
        }}
      >
        <div className="nz-fieldrow" style={{ marginBottom: 8 }}>
          <label>Послуга</label>
          <input
            className="nz-input"
            value={service}
            onChange={(ev) => setService(ev.target.value)}
            placeholder="напр. Манікюр"
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <div className="nz-fieldrow" style={{ marginBottom: 0 }}>
            <label>Сума, ₴</label>
            <input
              className="nz-input"
              value={amount}
              onChange={(ev) => setAmount(ev.target.value)}
              inputMode="numeric"
            />
          </div>
          <div className="nz-fieldrow" style={{ marginBottom: 0 }}>
            <label>Чай, ₴</label>
            <input
              className="nz-input"
              value={tip}
              onChange={(ev) => setTip(ev.target.value)}
              inputMode="numeric"
            />
          </div>
          <div className="nz-fieldrow" style={{ marginBottom: 0 }}>
            <label>Знижка, %</label>
            <input
              className="nz-input"
              value={discount}
              onChange={(ev) => setDiscount(ev.target.value)}
              inputMode="numeric"
            />
          </div>
        </div>
        <div className="nz-script-actions" style={{ marginTop: 10 }}>
          <button
            className={"nz-btn green flex" + (busy ? " disabled" : "")}
            onClick={save}
            style={{ height: 38 }}
          >
            ✓ Зберегти
          </button>
          <button
            className="nz-btn ghost"
            onClick={() => {
              setService(entry.service ?? "");
              setAmount(String(entry.amount ?? 0));
              setTip(String(entry.tip ?? 0));
              setDiscount(String(entry.discountPercent ?? 0));
              setEditing(false);
            }}
            style={{ height: 38 }}
          >
            ✕
          </button>
        </div>
        {/* Видалення рядка живе всередині форми редагування — щоб випадковим
            тапом по рядку нічого не знести. Бекенд (DELETE /cash/:id) і
            раніше дозволяв це адміну; кнопки просто не було в новому
            дизайні, лише в старому. */}
        {!entry.inPayout && (
          <button
            className="nz-btn ghost"
            onClick={remove}
            disabled={busy}
            style={{
              height: 34,
              width: "100%",
              marginTop: 8,
              color: "var(--red-ink)",
              background: "var(--red-bg)",
              borderColor: "#F6D5D5",
            }}
          >
            🗑 Видалити рядок
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        borderTop: "1px solid var(--line-2)",
        padding: "6px 0 4px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <button
        onClick={() => canEdit && setEditing(true)}
        style={{
          background: "transparent",
          border: 0,
          cursor: canEdit ? "pointer" : "default",
          fontFamily: "inherit",
          fontSize: 12.5,
          textAlign: "left",
          color: "var(--ink-2)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 6,
          width: "100%",
          padding: 0,
        }}
      >
        <span style={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {entry.service ?? "—"}
          {entry.isWarranty ? " (гарантія)" : ""}
          {entry.isBlogger ? " (блогер)" : entry.isBarter ? " (бартер)" : ""}
          {entry.isRedo
            ? entry.discountPercent && entry.discountPercent < 100
              ? ` 🛠 переробка ${entry.discountPercent}%`
              : " 🛠 переробка"
            : ""}
          {!entry.isRedo && entry.discountPercent > 0
            ? ` −${entry.discountPercent}%`
            : ""}
        </span>
        <span style={{ fontWeight: 700, color: "var(--ink)", whiteSpace: "nowrap" }}>
          {uah(entry.amount)} ₴
          {entry.tip > 0 ? ` + ${uah(entry.tip)} чай` : ""}
          {canEdit && (
            <span style={{ color: "var(--rose-500)", marginLeft: 6, fontSize: 11 }}>✏️</span>
          )}
        </span>
      </button>
      {/* Кнопка «позначити як розтрату» — owner only, видно лише поки запис
          не у виплаті і не блогер. Click toggle isRedo. */}
      {canRedo && !entry.inPayout && !entry.isBlogger && !entry.isWarranty && (
        <button
          onClick={async () => {
            try {
              const next = !entry.isRedo;
              await api.setCashRedo(entry.id, next);
              onSaved({ ...entry, isRedo: next });
            } catch (e) {
              alert(String(e));
            }
          }}
          style={{
            background: entry.isRedo ? "rgba(245, 158, 11, 0.15)" : "transparent",
            border: entry.isRedo
              ? "1px solid rgba(245, 158, 11, 0.5)"
              : "1px dashed rgba(245, 158, 11, 0.45)",
            color: entry.isRedo ? "#92400e" : "#b45309",
            fontFamily: "inherit",
            fontSize: 11.5,
            fontWeight: 700,
            padding: "4px 10px",
            borderRadius: 8,
            cursor: "pointer",
            alignSelf: "flex-start",
          }}
          title={
            entry.isRedo
              ? "Прибрати позначку «переробка»"
              : "Позначити як переробку: салон 0, майстру 40% з кишені Люби"
          }
        >
          {entry.isRedo ? "✖ Прибрати «переробка»" : "🛠 Позначити як розтрату (переробка)"}
        </button>
      )}
    </div>
  );
}


function MasterBlock({
  m,
  onOpen,
  onToggleStar,
  onOpenReports,
}: {
  m: MasterRow;
  onOpen: () => void;
  onToggleStar: () => void;
  onOpenReports?: () => void;
}) {
  const last = m.lastVisitAt
    ? new Date(m.lastVisitAt).toLocaleDateString("uk-UA", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })
    : "ще не була";
  // Підтвердження зірочки — раніше тап одразу перемикав, через що
  // замовниця ловила «мискліки» (їй цитата: «бо інколи такий місклік,
  // я єбу»). Тепер відкривається bottom-sheet з явною дією і кнопкою
  // «Скасувати».
  const [starConfirm, setStarConfirm] = useState(false);
  return (
    <div className="nz-mblock">
      <button className="nz-mblock-head" onClick={onOpen}>
        <button
          className="nz-star"
          data-on={m.starred}
          onClick={(e) => {
            e.stopPropagation();
            setStarConfirm(true);
          }}
        >
          ⭐
        </button>
        <div className="nz-row-main">
          <div className="nz-row-name">
            {m.name}
            {!m.active && (
              <span
                className="nz-chip ignore"
                style={{ marginLeft: 6, fontSize: 10 }}
              >
                🔒
              </span>
            )}
          </div>
          <div className="nz-row-sub">
            Останній візит: {last} · {m.telegramUsername ? "@" + m.telegramUsername : "TG " + (m.telegramId ?? "—")}
          </div>
        </div>
        <span style={{ color: "var(--ink-3)", flexShrink: 0 }}>
          <ChevronRight />
        </span>
      </button>
      {onOpenReports && (
        <button className="nz-mblock-btn" onClick={onOpenReports}>
          📋 Звіти каси · переглянути / редагувати
        </button>
      )}

      <ConfirmSheet
        open={starConfirm}
        onClose={() => setStarConfirm(false)}
        title={
          m.starred
            ? `Прибрати зірочку у «${m.name}»?`
            : `Зробити «${m.name}» зірочковою?`
        }
        body={
          m.starred ? (
            <>
              <p style={{ margin: "0 0 6px" }}>
                Зараз у неї <b>⭐ Своя</b> — вона у пріоритеті в групуванні,
                її візити підсвічуються кольором в календарі, у фільтрах
                по майстру вона першою у групі «Свої».
              </p>
              <p style={{ margin: 0 }}>
                Прибрати? Вона переїде до групи «Інші», кольорова мітка
                зникне.
              </p>
            </>
          ) : (
            <>
              <p style={{ margin: "0 0 6px" }}>
                Додасть її до групи <b>⭐ Свої</b>: візити підсвічуються
                кольором у календарі Заплановані, у dropdown-фільтрах вона
                буде нагорі, у звітах виділяється.
              </p>
              <p style={{ margin: 0 }}>
                Це безпечно — зірочку можна повернути будь-коли.
              </p>
            </>
          )
        }
        confirmLabel={m.starred ? "⭐ Прибрати зірочку" : "⭐ Зробити своєю"}
        tone={m.starred ? "danger" : "primary"}
        onConfirm={async () => {
          await onToggleStar();
          setStarConfirm(false);
        }}
      />
    </div>
  );
}

export function MasterDetailModern({
  id,
  onBack,
}: {
  id: string;
  onBack: () => void;
}) {
  const [data, setData] = useState<any | null>(null);
  const [range, setRange] = useState<PeriodValue>({ preset: "1m" });
  const [source, setSource] = useState<"cash" | "bumpix">("cash");
  const [photos, setPhotos] = useState<
    { id: string; url: string; caption: string | null }[]
  >([]);
  const [photoTotal, setPhotoTotal] = useState(0);
  const [photoPage, setPhotoPage] = useState(1);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [advanceNote, setAdvanceNote] = useState("");
  const [advanceBusy, setAdvanceBusy] = useState(false);
  const [tgOpen, setTgOpen] = useState(false);
  const [toast, setToast] = useState<ToastMsg | null>(null);
  const PHOTOS_PER_PAGE = 12;

  useEffect(() => {
    api.me().then((m: any) => setIsOwner(!!m?.isOwner)).catch(() => setIsOwner(false));
  }, []);

  async function submitAdvance() {
    const amt = Number(advanceAmount.replace(",", "."));
    if (!Number.isFinite(amt) || amt <= 0) {
      alert("Введи додатню суму авансу");
      return;
    }
    setAdvanceBusy(true);
    try {
      await api.giveAdvance(id, amt, advanceNote.trim() || undefined);
      setAdvanceOpen(false);
      setAdvanceAmount("");
      setAdvanceNote("");
      loadStats();
      const amtFmt = uahFull(amt);
      const masterName = data?.master?.name ?? "майстру";
      setToast({
        tone: "gold",
        text: (
          <>
            🪙 Аванс <b>−{amtFmt} ₴</b> видано майстру <b>{masterName}</b>! 💸
          </>
        ),
      });
    } catch (e) {
      alert(String(e));
    } finally {
      setAdvanceBusy(false);
    }
  }

  const loadStats = () => {
    const { from, to } = periodToIsoRange(range);
    api.masterStats(id, from, to).then(setData).catch(() => setData(null));
  };

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, range]);
  // Зміна майстра — назад на першу сторінку портфоліо.
  useEffect(() => {
    setPhotoPage(1);
  }, [id]);

  // Портфоліо тягнеться СТОРІНКАМИ з сервера (раніше бралось перших 100 і
  // різалось на клієнті — решта фото були недосяжні взагалі).
  useEffect(() => {
    let cancelled = false;
    setPhotoBusy(true);
    api
      .masterPhotos(id, PHOTOS_PER_PAGE, (photoPage - 1) * PHOTOS_PER_PAGE)
      .then((r) => {
        if (cancelled) return;
        setPhotos(
          r.items.map((x) => ({ id: x.id, url: x.url, caption: x.caption })),
        );
        setPhotoTotal(r.total);
      })
      .catch(() => {
        if (cancelled) return;
        setPhotos([]);
        setPhotoTotal(0);
      })
      .finally(() => {
        if (!cancelled) setPhotoBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, photoPage]);

  if (!data) {
    return (
      <>
        <button className="nz-backbtn" onClick={onBack}>
          ← Назад
        </button>
        <div className="hint" style={{ marginTop: 14 }}>Завантаження…</div>
      </>
    );
  }

  const m = data.master;
  const cash = source === "cash";
  const revenue = cash ? data.cashRevenue : data.bumpixRevenue;
  const avg = cash ? data.cashAvgCheck : data.bumpixAvgCheck;
  const tips = data.cashTips ?? 0;
  // 40% майстрині — від ПОВНОЇ суми послуг (сервер: masterServicesTakes),
  // а не від «живих грошей» cashRevenue: блогерку їй теж оплачують. Та сама
  // регресія з v0.30.4, що й у «Грошах».
  const masterShare =
    cash && typeof data.masterServicesTakes === "number"
      ? data.masterServicesTakes
      : Math.round(revenue * 0.4);
  const studioShare = Math.round(revenue * 0.6);

  return (
    <>
      <button className="nz-backbtn" onClick={onBack}>
        ← Майстри
      </button>

      <div className="nz-hero">
        <div className="nz-hero-av" style={{ background: nzAvatar(m.name) }}>
          {nzInitials(m.name)}
          {m.starred && <span className="nz-hero-star">⭐</span>}
        </div>
        <div className="nz-hero-id">
          <div className="nz-hero-name">{m.name}</div>
          <div className="nz-hero-sub">
            {m.telegramUsername ? "@" + m.telegramUsername : null}
            {m.telegramId ? ` · TG ${m.telegramId}` : ""}
          </div>
          <div className="nz-hero-pills">
            {m.active ? (
              <span className="nz-chip new">Активна</span>
            ) : (
              <span className="nz-chip ignore">🔒 Неактивна</span>
            )}
            <span className="nz-chip">{data.cashEntries ?? 0} звітів</span>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setTgOpen(true)}
        className="nz-tgbtn"
        data-set={!!m.telegramId}
      >
        {m.telegramId ? "📱 Змінити Telegram ID" : "📱 Задати Telegram ID"}
      </button>

      <PeriodPicker value={range} onChange={setRange} />

      <div className="nz-segwrap" style={{ marginTop: 13 }}>
        <button className="nz-seg" data-on={cash} onClick={() => setSource("cash")}>
          💰 Telegram
        </button>
        <button className="nz-seg" data-on={!cash} onClick={() => setSource("bumpix")}>
          📊 Bumpix
        </button>
      </div>

      <div className="nz-kpis">
        <Kpi ic="📅" label="Візитів" value={String(data.visits ?? 0)} sub={`${data.cashEntries} записів каси`} />
        <Kpi ic="👥" label="Клієнтів" value={String(data.uniqueClients ?? 0)} sub="унікальних" />
        <Kpi ic="🆕" label="Нових" value={String(data.newClients ?? 0)} sub="вперше до неї" />
        <Kpi ic="💰" label="Виручка" value={`${uah(revenue)} ₴`} sub={`ср. чек ${uah(avg)}`} cls="green" />
      </div>

      {cash && (
        <div className="nz-kpis k3">
          <Kpi label="🎁 40%" value={`${uah(masterShare)}`} cls="green" />
          <Kpi label="💸 Чай" value={`${uah(tips)}`} cls="green" />
          <Kpi label="🪑 60%" value={`${uah(studioShare)}`} />
        </div>
      )}

      {data.retention && (
        <div className="nz-sec">
          <div className="nz-sec-title">📈 Retention · період</div>
          <div style={{ marginTop: 12 }}>
            <Ring
              pct={data.retention.rate}
              label={`${data.retention.returned} з ${data.retention.newClients} повернулись`}
              sub="новеньких прийшли до неї ще раз"
            />
          </div>
        </div>
      )}

      {cash &&
        ((data.pendingFromPrices ?? 0) > 0 ||
          (data.pendingTips ?? 0) > 0 ||
          (data.outstandingAdvances ?? 0) > 0 ||
          isOwner) && (
        <div className="nz-payout">
          <div className="lbl">💸 До виплати</div>
          <div
            className="amt"
            style={{
              color: data.pendingPayout < 0 ? "var(--red-ink)" : undefined,
            }}
          >
            {uahFull(data.pendingPayout ?? 0)} ₴
            {data.pendingPayout < 0 && (
              <span style={{ fontSize: 13, marginLeft: 8 }}>(борг)</span>
            )}
          </div>
          <div className="nz-payout-rows">
            <div className="nz-sumrow">
              <span className="k">Послуги · 40%</span>
              <span className="vv">{uahFull(data.pendingFromPrices ?? 0)} ₴</span>
            </div>
            <div className="nz-sumrow">
              <span className="k">Чайові · 100%</span>
              <span className="vv">{uahFull(data.pendingTips ?? 0)} ₴</span>
            </div>
            {(data.outstandingAdvances ?? 0) > 0 && (
              <div className="nz-sumrow">
                <span className="k">🪙 Аванс (видано)</span>
                <span className="vv" style={{ color: "var(--rose-700)" }}>
                  −{uahFull(data.outstandingAdvances)} ₴
                </span>
              </div>
            )}
          </div>
          {isOwner ? (
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="nz-paybtn"
                disabled={paying || (data.pendingPayout ?? 0) <= 0}
                style={{ flex: 2 }}
                onClick={async () => {
                  if (paying) return;
                  if ((data.pendingPayout ?? 0) <= 0) return;
                  const prices = data.pendingFromPrices ?? 0;
                  const tips = data.pendingTips ?? 0;
                  const adv = data.outstandingAdvances ?? 0;
                  const parts = [
                    prices > 0 ? `${uahFull(prices)} з послуг` : null,
                    tips > 0 ? `${uahFull(tips)} чайові` : null,
                    adv > 0 ? `мінус ${uahFull(adv)} авансу` : null,
                  ].filter(Boolean);
                  if (!window.confirm(
                    `Виплатити ${uahFull(data.pendingPayout)} грн для ${m.name}?\n\n${parts.join(" + ")}`,
                  )) return;
                  setPaying(true);
                  const paidAmount = data.pendingPayout ?? 0;
                  try {
                    const { from, to } = periodToIsoRange(range);
                    await api.payoutMaster(id, from, to);
                    loadStats();
                    setToast({
                      tone: "green",
                      text: (
                        <>
                          💳 Виплачено <b>{uahFull(paidAmount)} ₴</b> майстру{" "}
                          <b>{m.name}</b>! 🎉
                        </>
                      ),
                    });
                  } catch (e) {
                    alert(String(e));
                  } finally {
                    setPaying(false);
                  }
                }}
              >
                {paying
                  ? "Зачекай…"
                  : (data.pendingPayout ?? 0) > 0
                    ? `💳 Виплатити ${uahFull(data.pendingPayout)} ₴`
                    : "💳 Нема що виплачувати"}
              </button>
              <button
                type="button"
                className="nz-advbtn"
                style={{ flex: 1 }}
                onClick={() => setAdvanceOpen(true)}
              >
                🪙 АВАНС
              </button>
            </div>
          ) : (
            <div className="hint" style={{ marginTop: 8, fontSize: 12 }}>
              <em>Виплати робить Люба — це для перегляду.</em>
            </div>
          )}
        </div>
      )}

      {photoTotal > 0 && (() => {
        const totalPages = Math.max(1, Math.ceil(photoTotal / PHOTOS_PER_PAGE));
        const page = Math.min(photoPage, totalPages);
        return (
          <div className="nz-sec">
            <div className="nz-sec-title">📸 Портфоліо · {photoTotal}</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3,1fr)",
                gap: 6,
                marginTop: 12,
                opacity: photoBusy ? 0.5 : 1,
                transition: "opacity .15s",
              }}
            >
              {photos.map((p) => (
                <a
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noopener"
                  style={{
                    display: "block",
                    aspectRatio: "1 / 1",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "var(--rose-50)",
                    border: "1px solid var(--rose-100)",
                  }}
                >
                  <img
                    src={p.url}
                    alt={p.caption ?? ""}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </a>
              ))}
            </div>
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 10,
                  gap: 8,
                }}
              >
                <button
                  className="nz-btn"
                  onClick={() => setPhotoPage(page - 1)}
                  disabled={page <= 1 || photoBusy}
                  style={{ padding: "6px 14px", height: 36 }}
                >
                  ← Назад
                </button>
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--ink-3)",
                    fontWeight: 700,
                  }}
                >
                  стор. {page} з {totalPages}
                </span>
                <button
                  className="nz-btn"
                  onClick={() => setPhotoPage(page + 1)}
                  disabled={page >= totalPages || photoBusy}
                  style={{ padding: "6px 14px", height: 36 }}
                >
                  Далі →
                </button>
              </div>
            )}
          </div>
        );
      })()}

      <button
        className="nz-btn danger-ghost"
        style={{ width: "100%", marginTop: 14, height: 46 }}
        onClick={async () => {
          const verb = m.active ? "закрити" : "відновити";
          if (!window.confirm(`Точно ${verb} доступ?`)) return;
          await api.setMasterActive(id, !m.active);
          window.location.reload();
        }}
      >
        {m.active ? "🔒 Закрити доступ до бота" : "🔓 Відновити доступ"}
      </button>

      {advanceOpen && (
        <div className="nz-dim" onClick={() => !advanceBusy && setAdvanceOpen(false)}>
          <div
            className="nz-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="nz-sheet-grip" />
            <div className="nz-sheet-title">🪙 Аванс — {m.name}</div>
            <div className="nz-confirm-body">
              <p style={{ margin: "0 0 8px" }}>
                Введи суму авансу. Вона <b>одразу зменшить</b> наступну виплату
                і <b>додасться у Витрати</b> (категорія «🪙 Аванси майстрам»).
              </p>
              {(data.pendingPayout ?? 0) > 0 && (
                <p
                  className="hint"
                  style={{ margin: "0 0 8px", fontSize: 12 }}
                >
                  Зараз майстру належить: <b>{uahFull(data.pendingPayout)} ₴</b>.
                  Можеш дати більше — підемо в борг, погаситься з майбутніх
                  заробітків.
                </p>
              )}
              <div className="nz-fieldrow" style={{ marginBottom: 10 }}>
                <label>Сума, ₴</label>
                <input
                  className="nz-input"
                  inputMode="decimal"
                  autoFocus
                  value={advanceAmount}
                  onChange={(e) =>
                    setAdvanceAmount(e.target.value.replace(/[^0-9.,]/g, ""))
                  }
                  placeholder="напр. 1500"
                  style={{ fontSize: 20, fontWeight: 800 }}
                />
              </div>
              <div className="nz-fieldrow" style={{ marginBottom: 4 }}>
                <label>Коментар (необовʼязково)</label>
                <input
                  className="nz-input"
                  value={advanceNote}
                  onChange={(e) => setAdvanceNote(e.target.value)}
                  placeholder="напр. за матеріали"
                />
              </div>
            </div>
            <button
              type="button"
              className="nz-confirm-btn-primary"
              disabled={advanceBusy || !advanceAmount.trim()}
              onClick={submitAdvance}
            >
              {advanceBusy
                ? "Видаю…"
                : `🪙 Видати аванс${advanceAmount.trim() ? ` ${advanceAmount} ₴` : ""}`}
            </button>
            <button
              type="button"
              className="nz-sheet-cancel"
              disabled={advanceBusy}
              onClick={() => setAdvanceOpen(false)}
            >
              Скасувати
            </button>
          </div>
        </div>
      )}

      {tgOpen && (
        <MasterTelegramSheet
          masterId={id}
          masterName={m.name}
          currentTgId={m.telegramId}
          currentUsername={m.telegramUsername}
          onClose={() => setTgOpen(false)}
          onSaved={(msg) => {
            setTgOpen(false);
            setToast({ text: msg, tone: "green" });
            loadStats();
          }}
        />
      )}

      <Toast msg={toast} onClose={() => setToast(null)} />
    </>
  );
}

// ---- 📱 Задати / змінити Telegram ID майстра ----

function MasterTelegramSheet({
  masterId,
  masterName,
  currentTgId,
  currentUsername,
  onClose,
  onSaved,
}: {
  masterId: string;
  masterName: string;
  currentTgId: string | null;
  currentUsername: string | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [tgId, setTgId] = useState(currentTgId ?? "");
  const [tgUsername, setTgUsername] = useState(currentUsername ?? "");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      await api.setMasterTelegram(masterId, {
        telegramId: tgId.trim(),
        telegramUsername: tgUsername.trim() || null,
      });
      onSaved(
        tgId.trim()
          ? `📱 Telegram привʼязано до ${masterName}!`
          : `Telegram відвʼязано у ${masterName}`,
      );
    } catch (e) {
      alert(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="nz-dim" onClick={onClose}>
      <div
        className="nz-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="nz-sheet-grip" />
        <div className="nz-sheet-title">📱 Telegram · {masterName}</div>

        <div className="nz-fieldrow">
          <label>Telegram ID</label>
          <input
            value={tgId}
            onChange={(e) => setTgId(e.target.value.replace(/[^0-9]/g, ""))}
            inputMode="numeric"
            placeholder="напр. 1618702437"
            style={fieldInputStyle}
          />
          <div className="hint">
            Хай майстер напише боту <b>/myid</b> — і скине тобі це число.
            Порожнє поле = відвʼязати.
          </div>
        </div>

        <div className="nz-fieldrow">
          <label>Telegram username</label>
          <input
            value={tgUsername}
            onChange={(e) => setTgUsername(e.target.value.replace(/^@/, ""))}
            placeholder="без @ (необовʼязково)"
            style={fieldInputStyle}
          />
        </div>

        <button
          className="nz-btn primary"
          disabled={busy}
          onClick={save}
          style={{ width: "100%", height: 46, marginTop: 8 }}
        >
          {busy ? "Зберігаю…" : "Зберегти"}
        </button>
      </div>
    </div>
  );
}

// ---- ➕ Додати майстра (bottom-sheet) ----

function AddMasterSheet({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [tgId, setTgId] = useState("");
  const [tgUsername, setTgUsername] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await api.upsertMaster({
        name: name.trim(),
        telegramId: tgId.trim() || undefined,
        telegramUsername:
          tgUsername.trim().replace(/^@/, "") || undefined,
      });
      onCreated();
    } catch (e) {
      alert(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="nz-dim" onClick={onClose}>
      <div
        className="nz-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="nz-sheet-grip" />
        <div className="nz-sheet-title">➕ Новий майстер</div>

        <div className="nz-fieldrow">
          <label>Імʼя *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Наприклад: Марія"
            style={fieldInputStyle}
          />
        </div>

        <div className="nz-fieldrow">
          <label>Telegram ID</label>
          <input
            value={tgId}
            onChange={(e) => setTgId(e.target.value.replace(/[^0-9]/g, ""))}
            inputMode="numeric"
            placeholder="напр. 295434"
            style={fieldInputStyle}
          />
          <div className="hint">
            Опціонально. Якщо вкажеш — майстер зможе зайти у міні-бота.
          </div>
        </div>

        <div className="nz-fieldrow">
          <label>Telegram username</label>
          <input
            value={tgUsername}
            onChange={(e) =>
              setTgUsername(e.target.value.replace(/^@/, ""))
            }
            placeholder="без @"
            style={fieldInputStyle}
          />
        </div>

        <button
          className="nz-btn primary"
          disabled={busy || !name.trim()}
          onClick={submit}
          style={{ width: "100%", height: 46, marginTop: 8 }}
        >
          {busy ? "Зберігаємо…" : "Створити майстра"}
        </button>
      </div>
    </div>
  );
}

const fieldInputStyle = {
  width: "100%",
  padding: "11px 13px",
  border: "1px solid var(--line)",
  borderRadius: 12,
  background: "var(--surface)",
  color: "var(--ink)",
  fontSize: 14,
  fontFamily: "inherit",
  boxSizing: "border-box",
} as const;

function Kpi({
  ic,
  label,
  value,
  sub,
  cls,
}: {
  ic?: string;
  label: string;
  value: string;
  sub?: string;
  cls?: "green" | "red" | "plum" | "blue" | "amber";
}) {
  return (
    <div className="nz-kpi">
      <div className="l">
        {ic && <span className="nz-kpi-ic">{ic}</span>}
        {label}
      </div>
      <div className={"v " + (cls ?? "")}>{value}</div>
      {sub && <div className="s">{sub}</div>}
    </div>
  );
}

function Ring({
  pct,
  label,
  sub,
}: {
  pct: number;
  label: string;
  sub: string;
}) {
  const r = 32;
  const c = 2 * Math.PI * r;
  const off = c * (1 - pct / 100);
  return (
    <div className="nz-ring">
      <svg className="nz-ring-svg" viewBox="0 0 88 88">
        <defs>
          <linearGradient id="nzRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--rose-400)" />
            <stop offset="100%" stopColor="var(--rose-600)" />
          </linearGradient>
        </defs>
        <circle cx="44" cy="44" r={r} fill="none" stroke="var(--rose-50)" strokeWidth="9" />
        <circle
          cx="44"
          cy="44"
          r={r}
          fill="none"
          stroke="url(#nzRing)"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={off}
          transform="rotate(-90 44 44)"
        />
        <text x="44" y="50" textAnchor="middle" fontSize="20" fontWeight="800" fill="var(--rose-700)">
          {pct}%
        </text>
      </svg>
      <div className="nz-ring-cap">
        <div className="rt">{label}</div>
        <div className="rs">{sub}</div>
      </div>
    </div>
  );
}

function ChevronRight() {
  return (
    <svg width="9" height="14" viewBox="0 0 9 14" fill="none">
      <path
        d="m1.5 1 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Точна сума до 1 грн з розділювачем тисяч ("16 057", не "16k") — люди
// плутались у заокруглених «k». Групування пробілами, знак зберігається.
function uah(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** Точне число з нерозривним пробілом між тисячами: 16 057. Для виплат,
 *  де округлення «16k» некоректне (потрібна копійка-в-копійку). */
function uahFull(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}
